import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { useTranslation } from 'react-i18next';
import api from '../api';

function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetMessage, setResetMessage] = useState('');
  const { login } = useAuth();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await login(email, password);
      navigate('/dashboard');
    } catch (err) {
      setError(err.response?.data?.detail || t('common.error'));
      // Keep email field, only clear password for security
      setPassword('');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setResetMessage('');
    setLoading(true);

    try {
      await api.post('/api/auth/request-reset', { email: resetEmail });
      setResetMessage(t('forgotPassword.success'));
      setTimeout(() => {
        setShowForgotPassword(false);
        setResetMessage('');
        setResetEmail('');
      }, 3000);
    } catch (err) {
      setResetMessage(t('forgotPassword.error'));
    } finally {
      setLoading(false);
    }
  };

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
    localStorage.setItem('language', lng);
  };

  return (
    <div style={{ 
      minHeight: '100vh', 
      display: 'flex', 
      alignItems: 'center', 
      justifyContent: 'center',
      background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)'
    }}>
      <div style={{
        maxWidth: '420px',
        width: '100%',
        padding: '2.5rem',
        background: 'white',
        borderRadius: '20px',
        boxShadow: '0 20px 60px rgba(0,0,0,0.15)'
      }}>
        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <img 
            src="/assets/vivendius-logo-color.png" 
            alt="Vivendius Logo" 
            style={{ height: '80px', margin: '0 auto 1rem' }}
          />
        </div>

        {!showForgotPassword ? (
          <form onSubmit={handleSubmit} style={{ display: 'grid', gap: '1.25rem' }} data-testid="login-form">
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: '600', color: '#374151' }}>
                {t('common.email')}
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                style={{
                  width: '100%',
                  padding: '0.875rem 1rem',
                  border: '2px solid #e5e7eb',
                  borderRadius: '10px',
                  fontSize: '0.95rem',
                  background: 'white',
                  color: '#1f2937'
                }}
                data-testid="email-input"
              />
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: '600', color: '#374151' }}>
                {t('common.password')}
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                style={{
                  width: '100%',
                  padding: '0.875rem 1rem',
                  border: '2px solid #e5e7eb',
                  borderRadius: '10px',
                  fontSize: '0.95rem',
                  background: 'white',
                  color: '#1f2937'
                }}
                data-testid="password-input"
              />
            </div>

            {error && (
              <div style={{ padding: '0.875rem', background: '#fee2e2', border: '1px solid #fca5a5', borderRadius: '8px', color: '#991b1b', fontSize: '0.875rem' }}>
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              style={{
                width: '100%',
                padding: '1rem',
                background: 'linear-gradient(135deg, #103766 0%, #1a4d7a 100%)',
                color: 'white',
                border: 'none',
                borderRadius: '12px',
                fontSize: '1rem',
                fontWeight: '600',
                cursor: 'pointer',
                boxShadow: '0 4px 12px rgba(16,55,102,0.3)'
              }}
              data-testid="login-button"
            >
              {loading ? t('common.loading') : t('common.login')}
            </button>

            <div style={{ textAlign: 'center' }}>
              <button
                type="button"
                onClick={() => setShowForgotPassword(true)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#103766',
                  fontSize: '0.875rem',
                  fontWeight: '500',
                  cursor: 'pointer',
                  textDecoration: 'underline'
                }}
                data-testid="forgot-password-link"
              >
                {t('forgotPassword.link')}
              </button>
            </div>
          </form>
        ) : (
          <div style={{ display: 'grid', gap: '1.25rem' }}>
            <div>
              <h3 style={{ fontSize: '1.25rem', fontWeight: '700', color: '#103766', marginBottom: '0.5rem' }}>
                {t('forgotPassword.title')}
              </h3>
              <p style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                {t('forgotPassword.description')}
              </p>
            </div>

            <form onSubmit={handleForgotPassword} style={{ display: 'grid', gap: '1rem' }}>
              <input
                type="email"
                value={resetEmail}
                onChange={(e) => setResetEmail(e.target.value)}
                required
                placeholder={t('common.email')}
                style={{
                  width: '100%',
                  padding: '0.875rem 1rem',
                  border: '2px solid #e5e7eb',
                  borderRadius: '10px',
                  fontSize: '0.95rem',
                  background: 'white',
                  color: '#1f2937'
                }}
                data-testid="reset-email-input"
              />

              {resetMessage && (
                <div style={{ padding: '0.875rem', background: '#d1fae5', border: '1px solid #6ee7b7', borderRadius: '8px', color: '#065f46', fontSize: '0.875rem' }}>
                  {resetMessage}
                </div>
              )}

              <div style={{ display: 'flex', gap: '0.75rem' }}>
                <button
                  type="button"
                  onClick={() => setShowForgotPassword(false)}
                  style={{
                    flex: 1,
                    padding: '0.875rem',
                    background: 'white',
                    border: '2px solid #e5e7eb',
                    borderRadius: '10px',
                    color: '#374151',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                  data-testid="cancel-reset-button"
                >
                  {t('common.cancel')}
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  style={{
                    flex: 1,
                    padding: '0.875rem',
                    background: 'linear-gradient(135deg, #007a33 0%, #00a843 100%)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '10px',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                  data-testid="submit-reset-button"
                >
                  {loading ? t('common.loading') : t('common.submit')}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Language Selector - Català FIRST */}
        <div style={{ marginTop: '1.5rem', display: 'flex', justifyContent: 'center', gap: '0.75rem' }}>
          <button
            onClick={() => changeLanguage('ca')}
            style={{
              padding: '0.625rem 1.25rem',
              borderRadius: '8px',
              background: i18n.language === 'ca' ? '#103766' : '#f3f4f6',
              color: i18n.language === 'ca' ? 'white' : '#6b7280',
              border: 'none',
              fontSize: '0.875rem',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
            data-testid="lang-ca"
          >
            Català
          </button>
          <button
            onClick={() => changeLanguage('es')}
            style={{
              padding: '0.625rem 1.25rem',
              borderRadius: '8px',
              background: i18n.language === 'es' ? '#103766' : '#f3f4f6',
              color: i18n.language === 'es' ? 'white' : '#6b7280',
              border: 'none',
              fontSize: '0.875rem',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
            data-testid="lang-es"
          >
            Español
          </button>
        </div>
      </div>
    </div>
  );
}

export default Login;
